import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import { useContext } from 'react';
import { MedialinkDialogHeaderTagsWithoutPillGroup } from '../../../../../../pages/material/components/MedialinkDialogHeaderTags';
import { ExerciseResult } from '../../@types/exercise-types';
import { useExerciseManagerContext } from '../../contexts/ExerciseManagerContext';
import { ExerciseTranslationsContext } from '../../contexts/ExerciseTranslationsContext';
import { getScoreAsPercentage } from '../../util/score-utils';
import ExerciseHighLightedItem from '../exercise-highlighted-item';

interface Props {
  result: ExerciseResult;
  onReview: (sessionId: string) => void;
  onRestart?: () => void;
}

function ProgressionModalCompletedSession({ result, onReview, onRestart }: Props) {
  const translations = useContext(ExerciseTranslationsContext);

  const { showScore, medialink, module, onClose } = useExerciseManagerContext();

  const percentage = getScoreAsPercentage(result.score.achieved, result.score.achievable);

  const { session } = result;

  return (
    <Dialog
      id="progression-modal-completed-session"
      modal
      onClose={onClose}
      aria-label={translations.progressionModalCompletedSession.a11yLabel}
    >
      <Dialog.Header
        title={translations.progressionModalCompletedSession.title}
        onClose={onClose}
        aria-label={translations.closeDialog}
      />
      <Dialog.Body>
        <div className="modal-content-text">
          {showScore ? (
            <>
              {medialink.unachievable ? (
                <>
                  <p>{translations.progressionModalCompletedSession.messages.showScore.adaptive.p1}</p>
                  <p>{translations.progressionModalCompletedSession.messages.showScore.adaptive.p2}</p>
                </>
              ) : percentage === 100 ? (
                <>
                  <p>{translations.progressionModalCompletedSession.messages.showScore.perfectResult.p1}</p>
                  <p>{translations.progressionModalCompletedSession.messages.showScore.perfectResult.p2}</p>
                </>
              ) : (
                <>
                  <p>{translations.progressionModalCompletedSession.messages.showScore.result.p1}</p>
                  <p>{translations.progressionModalCompletedSession.messages.showScore.result.p2}</p>
                </>
              )}
            </>
          ) : (
            <>
              {medialink.unachievable ? (
                <p>{translations.progressionModalCompletedSession.messages.adaptive}</p>
              ) : percentage > 66 ? (
                <p>{translations.progressionModalCompletedSession.messages.veryGood}</p>
              ) : percentage > 33 ? (
                <p>{translations.progressionModalCompletedSession.messages.good}</p>
              ) : (
                <p>{translations.progressionModalCompletedSession.messages.bad}</p>
              )}
            </>
          )}
        </div>
        <ExerciseHighLightedItem
          status="finished"
          medialink={medialink}
          title={translations.progressionModalCompletedSession.highlightedItem.title}
          scoreTitle={translations.progressionModalCompletedSession.highlightedItem.score}
          percentageTitle={translations.progressionModalCompletedSession.highlightedItem.percentage}
          result={{
            title: translations.progressionModalCompletedSession.highlightedItem.resultTitle,
            score: result.score,
          }}
          tags={{
            'aria-label': undefined,
            role: undefined,
            children: <MedialinkDialogHeaderTagsWithoutPillGroup module={module} material={medialink} />,
          }}
        />
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={
          onRestart
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Button prominence="neutral" onClick={onRestart}>
                    {translations.progressionModalCompletedSession.buttons.restart}
                  </Button>
                ),
              }
            : undefined
        }
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              {session && (
                <Button onClick={() => onReview(session)} prominence="neutral" key="neutral">
                  {translations.progressionModalCompletedSession.buttons.review}
                </Button>
              )}
              <Button onClick={onClose} prominence="success" key="success">
                {translations.progressionModalCompletedSession.buttons.close}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default ProgressionModalCompletedSession;
